import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { api } from "@/lib/api";
import { COMPANY } from "@/lib/company";

const PCS_LABEL = (it) => it.unit || "PCS";

let _logoDataUrlPromise = null;
function getLogoDataUrl() {
  if (_logoDataUrlPromise) return _logoDataUrlPromise;
  _logoDataUrlPromise = fetch(COMPANY.logoUrl)
    .then((r) => r.blob())
    .then((blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    }))
    .catch(() => null);
  return _logoDataUrlPromise;
}

async function fetchPhotoDataURL(path) {
  try {
    const r = await api.get(`/files/${path}`, { responseType: "blob" });
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(r.data);
    });
  } catch (e) {
    return null;
  }
}

/**
 * Build a clean, brutalist-styled order PDF with photo gallery.
 * Returns a Blob ready for download, share or upload.
 */
export async function buildOrderPdf(order, adminPhone = "") {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  const HEADER_H = 130;
  const RIGHT_BLOCK_W = 150;

  // Logo at top-left
  const logoData = await getLogoDataUrl();
  if (logoData) {
    try { doc.addImage(logoData, "PNG", margin, 14, 77, 77); } catch (e) { /* noop */ }
  }

  const companyX = margin + 84;
  const companyMaxW = pageW - margin - 84;  // full row 1 — company name only

  // Row 1: company legal name only, auto-fit to one line + underline
  doc.setTextColor(10, 10, 10);
  doc.setFont("helvetica", "bold");
  let nameSize = 14;
  while (nameSize > 8 && doc.getTextWidth((doc.setFontSize(nameSize), COMPANY.legalName)) > companyMaxW) {
    nameSize -= 0.5;
    doc.setFontSize(nameSize);
  }
  doc.setFontSize(nameSize);
  const nameY = 32;
  doc.text(COMPANY.legalName, companyX, nameY);
  const nameWidth = doc.getTextWidth(COMPANY.legalName);
  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(0.8);
  doc.line(companyX, nameY + 3, companyX + nameWidth, nameY + 3);

  // Rows below: address (left) and order meta (right, aligned to address rows, same font size)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const META_FONT = 9;
  const leftMaxW = pageW - margin - 84 - RIGHT_BLOCK_W - 12;
  const addrLines = doc.splitTextToSize(COMPANY.address, leftMaxW);
  const row1Y = nameY + 22;
  doc.text(addrLines, companyX, row1Y);
  const afterAddrY = row1Y + addrLines.length * 11;
  doc.text(`Contact: ${COMPANY.contact1} / ${COMPANY.contact2}   ·   Customer Care: ${COMPANY.customerCare}`, companyX, afterAddrY);
  doc.text(`${COMPANY.email}   ·   GST: ${COMPANY.gst}`, companyX, afterAddrY + 11);

  // Right block — order number / date / status, all at META_FONT size, aligned with address rows
  doc.setFont("helvetica", "bold");
  doc.setFontSize(META_FONT);
  doc.text(`ORDER ${order.order_no}`, pageW - margin, row1Y, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(META_FONT);
  doc.text(new Date(order.created_at).toLocaleString(), pageW - margin, row1Y + 12, { align: "right" });
  if (order.status) {
    doc.setFont("helvetica", "bold");
    doc.text(`STATUS: ${(order.status || "").toUpperCase()}`, pageW - margin, row1Y + 24, { align: "right" });
  }

  // Separator line below header
  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(1.2);
  doc.line(margin, HEADER_H, pageW - margin, HEADER_H);

  // Customer block
  let y = HEADER_H + 30;
  doc.setTextColor(10, 10, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("CUSTOMER", margin, y);
  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(1.5);
  doc.line(margin, y + 4, pageW - margin, y + 4);

  y += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const colW = (pageW - 2 * margin) / 2;
  doc.text(`Name:  ${order.customer.name}`, margin, y);
  doc.text(`City:  ${order.customer.city}`, margin + colW, y);
  y += 16;
  doc.text(`Phone: ${order.customer.phone}`, margin, y);
  doc.text(`Agent: ${order.customer.agent}${order.customer.agent_phone ? ` (${order.customer.agent_phone})` : ""}`, margin + colW, y);

  // Items table
  y += 24;
  const subtotal = order.total_amount || 0;
  const gstRate = order.gst_rate ?? 0.05;
  const gstAmount = order.gst_amount ?? +(subtotal * gstRate).toFixed(2);
  const grandTotal = order.grand_total ?? +(subtotal + gstAmount).toFixed(2);
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["#", "Design #", "Qty", "Unit", "Rate (Rs.)", "Amount (Rs.)"]],
    body: order.items.map((it, i) => [
      String(i + 1),
      it.design_number,
      String(it.quantity),
      PCS_LABEL(it),
      it.rate.toFixed(2),
      (it.quantity * it.rate).toFixed(2),
    ]),
    foot: [
      [
        "", "Subtotal",
        String(order.total_quantity),
        [order.total_pcs ? `${order.total_pcs} PCS` : "", order.total_sets ? `${order.total_sets} SETS` : ""].filter(Boolean).join(" + "),
        "",
        Number(subtotal).toFixed(2),
      ],
      [
        "", `GST @ ${Math.round(gstRate * 100)}%`, "", "", "",
        Number(gstAmount).toFixed(2),
      ],
      [
        "", "GRAND TOTAL", "", "", "",
        Number(grandTotal).toFixed(2),
      ],
    ],
    styles: { font: "helvetica", fontSize: 10, cellPadding: 6, lineColor: [10, 10, 10], lineWidth: 0.5, textColor: [10, 10, 10] },
    headStyles: { fillColor: [10, 10, 10], textColor: [255, 255, 255], fontStyle: "bold" },
    footStyles: { fillColor: [244, 244, 240], textColor: [10, 10, 10], fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 28 }, 2: { halign: "right" }, 3: { halign: "center" }, 4: { halign: "right" }, 5: { halign: "right" },
    },
  });
  let nextY = doc.lastAutoTable.finalY + 24;

  // Remarks
  if (order.remarks) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("REMARKS", margin, nextY);
    doc.setLineWidth(1.5);
    doc.line(margin, nextY + 4, pageW - margin, nextY + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(order.remarks, pageW - 2 * margin);
    doc.text(lines, margin, nextY + 22);
    nextY = nextY + 22 + lines.length * 12;
  }

  // Photo gallery (fetched as data URLs)
  const itemsWithPhotos = (order.items || []).filter((it) => !!it.photo_path);
  if (itemsWithPhotos.length) {
    nextY += 16;
    const pageH = doc.internal.pageSize.getHeight();
    if (nextY > pageH - 200) { doc.addPage(); nextY = 60; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("PHOTOS", margin, nextY);
    doc.setLineWidth(1.5);
    doc.line(margin, nextY + 4, pageW - margin, nextY + 4);
    nextY += 14;

    const colCount = 3;
    const gap = 10;
    const thumbW = (pageW - 2 * margin - gap * (colCount - 1)) / colCount;
    const thumbH = thumbW * 0.9;
    let col = 0;
    for (let i = 0; i < itemsWithPhotos.length; i++) {
      const it = itemsWithPhotos[i];
      const dataUrl = await fetchPhotoDataURL(it.photo_path);
      if (!dataUrl) continue;
      const x = margin + col * (thumbW + gap);
      const yPos = nextY + 4;
      if (yPos + thumbH + 26 > pageH - 40) { doc.addPage(); nextY = 60; col = 0; }
      try {
        const fmt = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
        doc.addImage(dataUrl, fmt, x, nextY + 4, thumbW, thumbH);
        doc.setDrawColor(10, 10, 10);
        doc.setLineWidth(1);
        doc.rect(x, nextY + 4, thumbW, thumbH);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(`${it.design_number} · ${it.quantity} ${PCS_LABEL(it)}`, x, nextY + thumbH + 18, { maxWidth: thumbW });
      } catch (e) { /* skip bad image */ }
      col = (col + 1) % colCount;
      if (col === 0) nextY = nextY + thumbH + 28;
    }
    if (col !== 0) nextY = nextY + thumbH + 28;
  }

  // Audit log
  if (Array.isArray(order.audit_log) && order.audit_log.length) {
    const pageH = doc.internal.pageSize.getHeight();
    if (nextY > pageH - 120) { doc.addPage(); nextY = 60; }
    nextY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("ACTION LOG", margin, nextY);
    doc.setLineWidth(1.5);
    doc.line(margin, nextY + 4, pageW - margin, nextY + 4);
    nextY += 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    order.audit_log.forEach((e) => {
      const when = new Date(e.at).toLocaleString();
      const line = `• [${(e.action || "").toUpperCase()}] ${when} — ${e.by_name || ""} <${e.by_email || ""}>${e.note ? ` — ${e.note}` : ""}`;
      const wrapped = doc.splitTextToSize(line, pageW - 2 * margin);
      doc.text(wrapped, margin, nextY);
      nextY += wrapped.length * 11 + 2;
    });
  }

  // Footer — slim, no company repeat
  const footY = doc.internal.pageSize.getHeight() - 30;
  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(0.5);
  doc.line(margin, footY - 10, pageW - margin, footY - 10);
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Created by ${order.created_by_name || ""}`, margin, footY);
  doc.text(`${COMPANY.appName}`, pageW - margin, footY, { align: "right" });

  return doc.output("blob");
}

export async function downloadOrderPdf(order, adminPhone) {
  const blob = await buildOrderPdf(order, adminPhone);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${order.order_no}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function shareOrderPdf(order, adminPhone, summaryText = "") {
  const blob = await buildOrderPdf(order, adminPhone);
  const file = new File([blob], `${order.order_no}.pdf`, { type: "application/pdf" });
  if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        title: `Order ${order.order_no}`,
        text: summaryText || `Order ${order.order_no}`,
        files: [file],
      });
      return true;
    } catch (e) {
      return false;
    }
  }
  return false;
}
